
<!-- src/components/DateRange.vue -->
<template>
  <div class="relative" ref="wrapper">
    

    <!-- Date Pickers -->
    <div
      class="flex flex-col mx-auto rounded-lg transition-all"
      :class="''"
    >
      <!-- Start and End Date Pickers -->
      <div class="flex flex-row">
        <!-- Start Date Picker -->
        <div
          :class="[
            'relative flex items-center cursor-pointer transition-colors duration-200',
            showEndDatePill ? 'mr-1 w-1/2' : 'w-full',
            props.compact ? 'h-[50px] rounded-md border border-gray-200 bg-white p-2' : 'rounded-lg border border-white/60 bg-white/80 p-1 sm:p-2',
            {
              'border-blue-400 shadow-lg': isCalendarOpen && resetType === 'start',
              'border-2 border-red-500 ring-1 ring-red-300': props.isError && !selectedDates.start,
              'border-white/60': !props.compact && !(isCalendarOpen && resetType === 'start') && !(props.isError && resetType === 'start'),
            }
          ]"
          @click="openCalendar('start')"
          @keydown.enter.prevent="openCalendar('start')"
          @keydown.space.prevent="openCalendar('start')"
          role="button"
          tabindex="0"
          aria-haspopup="dialog"
          :aria-expanded="isCalendarOpen"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="sm:h-8 sm:w-8 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <div class="flex flex-col s:ml-2 flex-nowrap whitespace-nowrap overflow-auto flex-grow">
            <!-- Conditional Label -->
            <span v-if="selectedDates.start" :class="[selectedTripType === 'one-way' && !exactDays ? 'text-blue-500 font-medium' : 'text-gray-400', 'text-xs']">{{ startDateLabel }}</span>
            <!-- Date Display -->
            <span
              :class="{
                'text-gray-700': selectedDates.start,
                'text-gray-400': !selectedDates.start
              }"
              class="text-xs sm:text-sm font-light"
            >
              {{ selectedDates.start ? formatDate(selectedDates.start) : startDateLabel }}
            </span>
          </div>
        </div>

        <!-- End Date Picker -->
        <div
          v-if="showEndDatePill"
          :class="[
            'relative w-1/2 flex items-center cursor-pointer transition-colors duration-200',
            props.compact ? 'h-[50px] rounded-md border border-gray-200 bg-white p-2' : 'rounded-lg border border-white/60 bg-white/80 p-1 sm:p-2',
            {
              'border-blue-400 shadow-lg': isCalendarOpen && resetType === 'end',
              'border-2 border-red-500 ring-1 ring-red-300': endDateHasError,
              'border-white/60': !props.compact && !(isCalendarOpen && resetType === 'end') && !endDateHasError,
            }
          ]"
          @click="openCalendar('end')"
          @keydown.enter.prevent="openCalendar('end')"
          @keydown.space.prevent="openCalendar('end')"
          role="button"
          tabindex="0"
          aria-haspopup="dialog"
          :aria-expanded="isCalendarOpen"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="sm:h-8 sm:w-8 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <div class="flex flex-col s:ml-2 whitespace-nowrap overflow-auto flex-grow flex-wrap">
            <!-- Conditional Label -->
            <span v-if="selectedDates.end" :class="[selectedTripType === 'one-way' && !exactDays ? 'text-blue-500 font-medium' : 'text-gray-400', 'text-xs']">{{ returnDate }}</span>
            <!-- Date Display -->
            <span
              :class="{
                'text-gray-700': selectedDates.end,
                'text-gray-400': !selectedDates.end
              }"
              class="text-xs sm:text-sm font-light"
            >
              {{ selectedDates.end ? formatDate(selectedDates.end) : returnDate }}
            </span>
          </div>
        
        </div>
      </div>
    </div>

    <!-- Date summary line (single element, text changes based on mode) -->
    <h1
      v-if="(selectedTripType === 'return' && nightsInterval && selectedDates.end) || (selectedTripType === 'one-way' && selectedDates.start)"
      :class="[
        'mt-1 text-[11px] sm:text-sm text-center mx-auto mb-1 whitespace-nowrap w-full px-1.5 sm:px-3 py-0.5 rounded-md cursor-pointer',
        nightsReviewActive
          ? 'text-white font-medium border-2 border-blue-400 bg-blue-500/30 animate-pulse-once'
          : (highlightDates
              ? 'text-white font-medium border-2 border-blue-600 bg-blue-500/20 animate-pulse-once'
              : 'text-white/90 font-light border border-white/30 bg-white/20 hover:bg-white/30')
      ]"
      @click="openCalendar('')"
    >
      <template v-if="selectedTripType === 'return'">
        <span class="font-semibold">{{ nightsInterval }}</span> at destination
      </template>
      <template v-else-if="selectedDates.end && selectedDates.start !== selectedDates.end">
        {{ t('searchForm.flightsBetween') }} <span class="font-semibold">{{ formatDate(selectedDates.start) }}</span> {{ t('searchForm.and') }} <span class="font-semibold">{{ formatDate(selectedDates.end) }}</span>
      </template>
      <template v-else>
        {{ t('searchForm.departOn') }} <span class="font-semibold">{{ formatDate(selectedDates.start) }}</span>
      </template>
    </h1>

    <!-- Trip Type Switcher -->
    <div class="flex items-center justify-end cursor-pointer transition-colors duration-200 mt-1"
      v-if="showTripTypeToggle && currentPage !== 'Results'"
    >
      <div class="flex w-full bg-white/20 rounded-lg border border-white/30 p-0.5 gap-0.5">
        <!-- One-Way Option -->
        <div
          class="flex-1 text-center cursor-pointer rounded transition-all duration-200 whitespace-nowrap font-semibold text-[11px] sm:text-sm px-1.5 sm:px-3 py-0.5"
          :class="selectedTripType === 'one-way' ? 'bg-blue-600 text-white shadow-md' : 'text-white/80 hover:bg-white/20'"
          @click="selectTripType('one-way')"
        >
          {{ t('searchForm.oneWay') }}
        </div>

        <!-- Return Option -->
        <div
          class="flex-1 text-center cursor-pointer rounded transition-all duration-200 whitespace-nowrap font-semibold text-[11px] sm:text-sm px-1.5 sm:px-3 py-0.5"
          :class="selectedTripType === 'return' ? 'bg-blue-600 text-white shadow-md' : 'text-white/80 hover:bg-white/20'"
          @click="selectTripType('return')"
        >
          {{ t('searchForm.return') }}
        </div>
      </div>
    </div>

    <!-- Background Overlay + Calendar Popup teleported to body to escape backdrop-blur stacking context -->
    <Teleport to="body">
    <div
      v-if="isCalendarOpen"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
      @click="closeCalendar"
    ></div>

    <!-- Calendar Popup – bottom sheet on mobile, centered card on desktop -->
    <div
      v-if="isCalendarOpen"
      class="fixed z-50 bg-white shadow-2xl flex flex-col overflow-hidden inset-0 w-full h-[100dvh] rounded-none lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-auto lg:h-auto lg:max-h-[92vh] lg:rounded-2xl lg:block lg:overflow-y-auto"
      ref="calendar"
      role="dialog"
      aria-modal="true"
      @click.stop
      @keyup.enter="closeCalendar"
    >
      <!-- ===== Top region: header + hint + pills + notice (fixed on mobile) ===== -->
      <div class="shrink-0 px-4 pt-4 lg:px-6 lg:pt-6">
      <!-- Header: date-mode toggle + close -->
      <div class="flex items-center justify-between mb-1.5">
        <div class="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-semibold rounded-md transition-all duration-200 border-2"
            :class="dateMode === 'range' ? 'bg-white text-blue-600 shadow-sm border-blue-500' : 'text-gray-500 hover:text-gray-700 border-transparent'"
            @click="dateMode = 'range'; exactDays = false"
          >{{ t('searchForm.dateRange') }}</button>
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-semibold rounded-md transition-all duration-200 border-2"
            :class="dateMode === 'exact' ? 'bg-white text-blue-600 shadow-sm border-blue-500' : 'text-gray-500 hover:text-gray-700 border-transparent'"
            @click="switchToExact()"
          >{{ t('searchForm.exactDates') }}</button>
        </div>
        <button type="button"
          @click="closeCalendar"
          aria-label="Close Calendar"
          class="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Contextual hint: explains what the selected mode lets you pick -->
      <div class="flex items-center gap-1 mb-3 pl-1 text-[11px] text-gray-400">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" transform="rotate(90 12 12)" />
        </svg>
        <transition name="hint" mode="out-in">
          <span :key="dateMode">
            <template v-if="dateMode === 'exact'">
              {{ t('searchForm.exactDates') }}: {{ selectedTripType === 'return' ? t('searchForm.exactDatesDescReturn') : t('searchForm.exactDatesDesc') }}
            </template>
            <template v-else>
              {{ t('searchForm.rangeDesc') }}
            </template>
          </span>
        </transition>
      </div>

      <!-- Selected date pills -->
      <div class="flex gap-2 mb-4">
        <!-- Start date pill -->
        <div
          class="flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer transition-colors"
          :class="[
            resetType === 'start' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300',
            showEndDatePill ? 'flex-1' : 'w-full'
          ]"
          @click="openCalendar('start')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <div class="flex flex-col min-w-0">
            <span class="text-xs font-semibold uppercase tracking-wide" :class="exactDays ? 'text-gray-400' : 'text-blue-500'">{{ startDateLabel }}</span>
            <span class="text-sm font-medium text-gray-700 truncate">{{ selectedDates.start ? formatDate(selectedDates.start) : '–' }}</span>
          </div>
          <button type="button" v-if="selectedDates.start" @click.stop="clearStartDate" class="ml-auto text-gray-300 hover:text-red-400 transition-colors flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- End date pill (hidden for exact + one-way) -->
        <div
          v-if="showEndDatePill"
          class="flex-1 flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer transition-colors"
          :class="resetType === 'end' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300'"
          @click="openCalendar('end')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <div class="flex flex-col min-w-0">
            <span class="text-xs font-semibold uppercase tracking-wide" :class="exactDays ? 'text-gray-400' : 'text-blue-500'">{{ returnDate }}</span>
            <span class="text-sm font-medium text-gray-700 truncate">{{ selectedDates.end ? formatDate(selectedDates.end) : '–' }}</span>
          </div>
          <button type="button" v-if="selectedDates.end" @click.stop="clearEndDate" class="ml-auto text-gray-300 hover:text-red-400 transition-colors flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </div>

      <!-- Weather forecast notice -->
      <div v-if="weatherNotice" class="mb-3 rounded-lg border px-2.5 py-2 text-[11px] leading-4 border-amber-200 bg-amber-50 text-amber-800">
        {{ weatherNotice }}
      </div>
      </div><!-- /Top region -->

      <!-- ===== Middle region: calendar (fills space, scrolls on mobile) ===== -->
      <div class="px-4 lg:px-6 flex-1 min-h-0 overflow-y-auto lg:flex-none lg:overflow-visible">

      <!-- Desktop: side arrows + 2-month grid -->
      <div v-if="isLargeScreen" class="relative mb-2 flex items-center gap-1"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
        @wheel="handleWheel"
      >
        <!-- Prev button (left side) -->
        <button type="button"
          @click="prevMonth"
          class="shrink-0 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-600"
          aria-label="Previous Month"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div class="flex-grow grid grid-cols-2 gap-6">
          <CalendarMonth
            v-for="(month, index) in visibleMonths"
            :key="index"
            :month="month"
            @selectDate="updateSelectedDates"
            @hoverDate="(d) => hoveredDate = d"
            @hoverLeave="hoveredDate = null"
            :selectedDates="selectedDates"
            :hoveredDate="hoveredDate"
            :isLargeScreen="true"
            :exactDays="exactDays"
            :isWeatherMode="weatherDayOptions.length > 0"
            :forecastEndDate="forecastEndDate"
            :dateMode="dateMode"
            :tripType="selectedTripType"
          />
        </div>

        <!-- Next button (right side) -->
        <button type="button"
          @click="nextMonth"
          class="shrink-0 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-600"
          aria-label="Next Month"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <!-- Mobile: vertically stacked, scrollable months -->
      <div v-else class="space-y-6 pb-2">
        <CalendarMonth
          v-for="(month, index) in scrollMonths"
          :key="index"
          :month="month"
          @selectDate="updateSelectedDates"
          @hoverDate="(d) => hoveredDate = d"
          @hoverLeave="hoveredDate = null"
          :selectedDates="selectedDates"
          :hoveredDate="hoveredDate"
          :isLargeScreen="false"
          :exactDays="exactDays"
          :isWeatherMode="weatherDayOptions.length > 0"
          :forecastEndDate="forecastEndDate"
          :dateMode="dateMode"
          :tripType="selectedTripType"
        />
      </div>

      </div><!-- /Middle region -->

      <!-- ===== Bottom region: nights card + actions (fixed on mobile) ===== -->
      <div class="shrink-0 px-4 pb-4 pt-2 lg:px-6 lg:pb-6 lg:pt-0 border-t border-gray-100 lg:border-t-0">

      <!-- Exact dates: simple nights info -->
      <div
        v-if="selectedTripType === 'return' && exactDays && selectedDates.start && selectedDates.end && !isSameDates"
        class="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-400"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
        <span class="font-medium text-gray-500">{{ nightsInterval }}</span> at destination
      </div>

      <!-- Premium Stay Duration Card (Primary UI for return trips, range mode) -->
      <transition name="fade">
        <div
          v-if="selectedTripType === 'return' && !exactDays && !isSameDates"
          ref="stayCard"
          class="mt-3 rounded-xl bg-gradient-to-b from-white to-gray-50/60 shadow-sm overflow-hidden transition-all duration-300"
          :class="nightsPrompted
            ? 'border-4 border-yellow-400 ring-2 ring-yellow-200 shadow-lg'
            : (selectedDates.start && selectedDates.end
                ? 'border-2 border-blue-400'
                : 'border border-gray-100')"
        >
          <!-- Nights display + presets header row -->
          <div class="px-4 pt-2.5 pb-1.5">
            <div class="flex items-center justify-center gap-2 flex-wrap">
              <span class="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                Nights at destination
              </span>
              <span class="text-lg font-bold text-gray-900 tracking-tight leading-none">{{ nightsInterval }}</span>
            </div>
            <p v-if="nightsPrompted" class="text-[11px] text-blue-600 text-center mt-1.5 font-medium">
              Happy with this? Adjust below or tap "Looks good".
            </p>
          </div>

          <!-- Slider + presets (flexible dates) -->
          <div class="px-4 pb-1.5">
            <!-- Quick-pick preset chips -->
            <div v-if="nightPresets.length" class="hidden lg:flex flex-wrap gap-1.5 justify-center mb-2">
              <button
                v-for="preset in nightPresets"
                :key="preset.label"
                type="button"
                @click="applyPreset(preset)"
                :class="isPresetActive(preset)
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'"
                class="px-2.5 py-0.5 text-xs font-medium rounded-full border transition-all duration-150 cursor-pointer"
              >
                {{ preset.label }}
              </button>
            </div>

            <!-- Slider row with min/max steppers -->
            <div class="flex items-center gap-2">
              <input
                v-model.number="minDays"
                name="min_days"
                class="text-center w-11 py-0.5 text-sm font-semibold text-gray-800 border border-gray-200 rounded-lg bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none transition"
                type="number"
                :min="0"
                :max="maxDays"
                aria-label="Minimum Days"
                @change="clampMinDays"
                @blur="clampMinDays"
              />

              <div class="flex-grow px-1">
                <RangeSlider
                  :min="0"
                  :max="maxSliderValue"
                  :startValue="minDays"
                  :endValue="maxDays"
                  @update:start="updateMinDays"
                  @update:end="updateMaxDays"
                />
              </div>

              <input
                v-model.number="maxDays"
                name="max_days"
                class="text-center w-11 py-0.5 text-sm font-semibold text-gray-800 border border-gray-200 rounded-lg bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none transition"
                type="number"
                :min="minDays"
                :max="maxSliderValue"
                aria-label="Maximum Days"
                @change="clampMaxDays"
                @blur="clampMaxDays"
              />
            </div>
          </div>

        </div>
      </transition>

      <!-- One-way / Return toggle above confirm -->
      <div v-if="showTripTypeToggle" class="mt-3 mb-2 text-right">
        <div class="border-t border-gray-200 mb-3 lg:w-2/3 lg:mx-auto"></div>
        <div class="flex justify-end bg-gray-100 rounded-lg p-0.5 gap-0.5 w-full lg:w-auto lg:inline-flex">
          <button
            type="button"
            class="flex-1 lg:flex-none px-4 py-1.5 text-sm font-semibold rounded-md transition-all duration-200"
            :class="selectedTripType === 'one-way' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
            @click="selectTripType('one-way')"
          >{{ t('searchForm.oneWay') }}</button>
          <button
            type="button"
            class="flex-1 lg:flex-none px-4 py-1.5 text-sm font-semibold rounded-md transition-all duration-200"
            :class="selectedTripType === 'return' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
            @click="selectTripType('return')"
          >{{ t('searchForm.return') }}</button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="mt-2 flex gap-3">
        <button
          @click="clearSelection"
          type="button"
          class="flex-1 py-2.5 border border-gray-200 text-sm font-medium rounded-xl text-gray-600 bg-white hover:bg-gray-50 transition-colors"
        >
          {{ t('searchForm.clearDates') }}
        </button>
        <button
          @click="confirmSelection"
          type="button"
          class="flex-1 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
        >
          {{ nightsPrompted ? t('searchForm.looksGood') : t('searchForm.confirmDates') }}
        </button>
      </div>
      </div><!-- /Bottom region -->
    </div>
    </Teleport>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  defineEmits,
  defineProps
} from 'vue';
import CalendarMonth from './CalendarMonth.vue';
import RangeSlider from './RangeSlider.vue';
import { useI18n } from 'vue-i18n';


// Define emits
const emit = defineEmits(['update-selected-dates', 'update-trip-type', 'update-range', 'update-exact-days', 'popup-closed', 'update-weather-days', 'nights-review']);
const { t, locale } = useI18n();

// Define props without destructuring to maintain reactivity
const props = defineProps({
  isError: {
    type: Boolean,
    default: false, // default value is false, meaning no error by default
  },
  selectedDates: {
    type: Object,
    default: () => ({ start: null, end: null }),
  },
  exactDays: {
    type: Boolean,
    default: false,
  },
  dateRange: {
    type: Object,
    default: () => ({ min: 3, max: 5 }),
  },
  preserveRange: {
    type: Boolean,
    default: false,
  },
  tripType: {
    type: String,
    default: 'return',
  },
  currentPage: {
    type: String,
    default: '',
  },
  compact: {
    type: Boolean,
    default: false,
  },
  showTripTypeToggle: {
    type: Boolean,
    default: true,
  },
  weatherDays: {
    type: Number,
    default: null,
  },
  weatherDayOptions: {
    type: Array,
    default: () => [],
  },
  weatherNotice: {
    type: String,
    default: '',
  },
  forecastEndDate: {
    type: String,
    default: null,
  },
  nightsReviewActive: {
    type: Boolean,
    default: false,
  },
  highlightDates: {
    type: Boolean,
    default: false,
  },
  defaultExactDates: {
    type: Boolean,
    default: false,
  },
});

const localWeatherDays = ref(props.weatherDays);
watch(() => props.weatherDays, (v) => { if (v != null) localWeatherDays.value = v; }, { immediate: true });

// Trip Type State
const selectedTripType = ref(props.tripType || 'return');

// Watch for prop changes
watch(
  () => props.tripType,
  (newType) => {
    console.log('Trip Type:', newType);
    if (newType) {
      selectedTripType.value = newType;
    }
  },
  { immediate: true }
);

// Reactive References
const selectedDates = ref({ start: null, end: null });
const hoveredDate = ref(null);
const isCalendarOpen = ref(false);
const calendar = ref(null);
const resetType = ref(null);
const currentMonth = ref(new Date().getMonth());
const currentYear = ref(new Date().getFullYear());
const wrapper = ref(null);


// Date mode: 'range' (flexible window) or 'exact' (specific dates)
const dateMode = ref(props.defaultExactDates || props.exactDays ? 'exact' : 'range');

// Exact Days Checkbox State
const exactDays = ref(props.defaultExactDates || props.exactDays);

// Start date label
const startDateLabel = computed(() => {
  if (dateMode.value === 'exact') {
    return selectedTripType.value === 'return' ? t('searchForm.departOn') : t('searchForm.flyOn');
  }
  return t('searchForm.departAfter');
});

// End date label
const returnDate = computed(() => {
  if (dateMode.value === 'exact') {
    return t('searchForm.returnOn');
  }
  return selectedTripType.value === 'return' ? t('searchForm.returnBefore') : t('searchForm.departBefore');
});



// Whether to show end date pill
const showEndDatePill = computed(() => {
  if (dateMode.value === 'exact' && selectedTripType.value === 'one-way') return false;
  return true;
});

const endDateHasError = computed(() =>
  props.isError &&
  !selectedDates.value.end &&
  (selectedTripType.value === 'return' || (!exactDays.value && !selectedDates.value.start))
);

// Responsive Design
const width = ref(window.innerWidth);
const isLargeScreen = computed(() => width.value >= 1024); // Adjust breakpoint as needed

const updateWindowSize = () => {
  width.value = window.innerWidth;
};

// Visible Months based on screen size – 2 on desktop, 1 on mobile (navigated with prev/next)
const visibleMonths = computed(() => {
  const count = isLargeScreen.value ? 2 : 1;
  const months = [];
  for (let i = 0; i < count; i++) {
    const month = new Date(currentYear.value, currentMonth.value + i);
    months.push(month);
  }
  return months;
});

// Mobile: a long, vertically-scrollable list of consecutive months covering the
// ~18-month booking window, so users can scroll up/down instead of paging.
const scrollMonths = computed(() => {
  const months = [];
  const base = new Date();
  const y = base.getFullYear();
  const m = base.getMonth();
  for (let i = 0; i < 18; i++) {
    months.push(new Date(y, m + i, 1));
  }
  return months;
});

const switchToExact = () => {
  dateMode.value = 'exact';
  exactDays.value = true;
  // For one-way exact, collapse to single date
  if (selectedTripType.value === 'one-way' && selectedDates.value.start) {
    selectedDates.value = { start: selectedDates.value.start, end: selectedDates.value.start };
    emit('update-selected-dates', selectedDates.value);
  }
};

// Methods
const openCalendar = (type) => {
  resetType.value = type;
  isCalendarOpen.value = true;
};

const closeCalendar = () => {
  isCalendarOpen.value = false;
  document.body.classList.remove('overflow-hidden');
  resetType.value = '';
  // Propagate any date changes made inside the calendar to the parent,
  // even when the user dismisses the popup without clicking "Confirm dates".
  emit('update-selected-dates', selectedDates.value);
  emit('update-range', { min: minDays.value, max: maxDays.value });
  console.log('Emitting popup-closed from closeCalendar');
  emit('popup-closed');
};

// Handler for keydown events
const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    closeCalendar();
  }
};

const updateSelectedDates = (newDates) => {
  selectedDates.value = newDates;
  console.log('Updating Dates: ', newDates);
  emit('update-selected-dates', newDates);
};

const confirmSelection = () => {
  const bothDates = selectedDates.value.start && selectedDates.value.end;
  // If it's a flexible return trip and the user hasn't reviewed the nights range,
  // nudge them once: highlight the card + switch the button to "Looks good".
  const needsNightsReview =
    selectedTripType.value === 'return' &&
    bothDates &&
    !isSameDates.value &&
    !exactDays.value &&
    !nightsManuallyEdited.value &&
    !nightsPrompted.value;

  if (needsNightsReview) {
    nightsPrompted.value = true;
    nextTick(() => {
      stayCard.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return;
  }

  // Emit the selected dates to the parent
  emit('update-selected-dates', selectedDates.value);
  emit('update-range', { min: minDays.value, max: maxDays.value });
  
  // Emit popup-closed event just like when clicking outside
  console.log('Emitting popup-closed from confirmSelection');
  emit('popup-closed');
  
  // Close the modal
  isCalendarOpen.value = false;
  nightsPrompted.value = false;
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString(locale.value === 'pl' ? 'pl-PL' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Touch swipe support for navigating months
let touchStartX = 0;
let touchStartY = 0;

const handleTouchStart = (e) => {
  touchStartX = e.touches[0].clientX;
  touchStartY = e.touches[0].clientY;
};

const handleTouchEnd = (e) => {
  const dx = e.changedTouches[0].clientX - touchStartX;
  const dy = e.changedTouches[0].clientY - touchStartY;
  // Only trigger if horizontal swipe is dominant and exceeds threshold
  if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
    if (dx < 0) nextMonth();
    else prevMonth();
  }
};

// Trackpad / horizontal-wheel navigation across months
let wheelAccum = 0;
let wheelCooldown = false;
const handleWheel = (e) => {
  // Only act when horizontal intent dominates (trackpad two-finger swipe)
  if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
  e.preventDefault();
  if (wheelCooldown) return;

  wheelAccum += e.deltaX;
  if (Math.abs(wheelAccum) < 40) return; // require a deliberate swipe

  if (wheelAccum > 0) nextMonth();
  else prevMonth();

  wheelAccum = 0;
  wheelCooldown = true;
  setTimeout(() => { wheelCooldown = false; }, 250);
};

const nextMonth = () => {
  currentMonth.value += 1;
  if (currentMonth.value > 11) {
    currentMonth.value = 0;
    currentYear.value += 1;
  }
};

const prevMonth = () => {
  const monthNow = new Date().getMonth();
  const yearNow = new Date().getFullYear();

  if (currentYear.value === yearNow && currentMonth.value === monthNow) {
    return;
  }

  currentMonth.value -= 1;

  if (currentMonth.value < 0) {
    currentMonth.value = 11;
    currentYear.value -= 1;
  }
};

const clearSelection = () => {
  selectedDates.value = { start: null, end: null };
  // adjust
  minDays.value = 3;
  maxDays.value = 5;
  exactDays.value = props.defaultExactDates;
  dateMode.value = props.defaultExactDates ? 'exact' : 'range';
  resetType.value = 'start';
  nightsManuallyEdited.value = false;
  nightsPrompted.value = false;
  emit('update-selected-dates', selectedDates.value);
};

// Trip Type Switch Method
const selectTripType = (type) => {
  const previousType = selectedTripType.value;
  selectedTripType.value = type;
  emit('update-trip-type', selectedTripType.value);
  nightsPrompted.value = false;

  // When switching to one-way in exact mode, collapse to single date
  if (type === 'one-way' && dateMode.value === 'exact' && selectedDates.value.start) {
    selectedDates.value = { start: selectedDates.value.start, end: selectedDates.value.start };
  }

  emit('update-selected-dates', selectedDates.value);

  const shouldReviewNights =
    previousType === 'one-way' &&
    type === 'return' &&
    selectedDates.value.start &&
    selectedDates.value.end &&
    !isSameDates.value &&
    !nightsManuallyEdited.value;

  if (shouldReviewNights) {
    nightsPrompted.value = true;
    nextTick(() => {
      stayCard.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }
};

// Handle Click Outside
const handleClickOutside = (event) => {
  if (isCalendarOpen.value && wrapper.value && !wrapper.value.contains(event.target)) {
    closeCalendar();
  }
};

// Lifecycle Hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', updateWindowSize);
  if (exactDays.value) {
    emit('update-exact-days', true);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', updateWindowSize);
});

// Calculate the number of days between two dates
const calculateDays = (start, end) => {
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Tracks whether the user has manually adjusted the nights slider/inputs
const nightsManuallyEdited = ref(false);
// Tracks whether we've nudged the user to review the nights range ("Looks good" step)
const nightsPrompted = ref(false);
// Template ref to the stay-duration card (for scroll-into-view on prompt)
const stayCard = ref(null);

// Reactive values for min and max days
const minDays = ref(3);
const maxDays = ref(5);
const defaultMaxDays = 14; // Default maximum days
const maxSliderValue = ref(defaultMaxDays); // Default maximum days

watch(
  () => props.dateRange,
  (newValue) => {
    console.log('DateRange: ', newValue);
    if (newValue && newValue.min != null && newValue.max != null) {
      minDays.value = newValue.min;
      maxDays.value = newValue.max;
      if (props.preserveRange) {
        nightsManuallyEdited.value = true;
      }
    }
  },
  { immediate: true, deep: true },
);

// Watch for changes in selectedDates to update maxSliderValue
watch(
  () => selectedDates.value,
  (newDates) => {
    if (newDates.start && newDates.end) {
      const startDate = new Date(newDates.start);
      const endDate = new Date(newDates.end);
      const days = calculateDays(startDate, endDate);
      
          // Update the maxSliderValue based on the number of days
      maxSliderValue.value = days;
      console.log('Days between dates:', days);
      
      // If the dates are the same (0 days), set both min and max to 0
      if (days === 0) {
        minDays.value = 0;
        maxDays.value = 0;
      } else if (!nightsManuallyEdited.value || (minDays.value === 0 && maxDays.value === 0)) {
        // Smart defaults: min ~50%, max ~70% of total available nights
        const smartMin = Math.max(1, Math.ceil(days * 0.5));
        const smartMax = Math.max(smartMin, Math.ceil(days * 0.7));
        minDays.value = Math.min(smartMin, days);
        maxDays.value = Math.min(smartMax, days);
      } else {
        // User has manually edited — just clamp to new slider range
        if (minDays.value > maxSliderValue.value) {
          minDays.value = Math.max(1, maxSliderValue.value - 1);
        }
        if (maxDays.value > maxSliderValue.value) {
          maxDays.value = maxSliderValue.value;
        }
      }
    }

    emit('update-range', { min: minDays.value, max: maxDays.value });

    // Changing the dates invalidates any prior "Looks good" nudge
    nightsPrompted.value = false;

    // Auto-advance the active pill indicator to guide the user
    if (!newDates.start) {
      resetType.value = 'start';
    } else if (!newDates.end) {
      resetType.value = 'end';
    } else {
      resetType.value = null;
    }
  },
  { immediate: true, deep: true },
);

// Watch for changes in exactDays to adjust nights at destination and if exactDays is selected emit the value
watch(
  () => exactDays.value,
  (newValue) => {
    if (newValue && selectedDates.value.start && selectedDates.value.end) {
      const nights = calculateDays(new Date(selectedDates.value.start), new Date(selectedDates.value.end));
      minDays.value = 0;
      maxDays.value = nights;
    }
    emit('update-exact-days', newValue);
    console.log('ExactDays: ', newValue);
  },
);

// Manage event listeners when the modal is open
watch(isCalendarOpen, (newVal) => {
  if (newVal) {
    // Add keydown event listener when modal opens
    document.addEventListener('keydown', handleKeydown);

    // Optionally, trap focus within the modal
    nextTick(() => {
      if (calendar.value) {
        calendar.value.focus();
      }
    });
  } else {
    // Remove keydown event listener when modal closes
    document.removeEventListener('keydown', handleKeydown);
  }
});

// Clean up in case the component is unmounted while the modal is open
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
});

// Check if start and end dates are the same
const isSameDates = computed(() => {
  if (selectedDates.value.start && selectedDates.value.end) {
    const startDate = new Date(selectedDates.value.start);
    const endDate = new Date(selectedDates.value.end);
    return calculateDays(startDate, endDate) === 0;
  }
  return false;
});

// Compute the nights interval display
const nightsInterval = computed(() => {
  if (selectedTripType.value === 'return') {
    // Check if the dates are the same (0 nights)
    if (isSameDates.value) {
      return '0 nights';
    }
    
    if (exactDays.value && selectedDates.value.start && selectedDates.value.end) {
      return maxSliderValue.value === 1 ? `${maxSliderValue.value} night` : `${maxSliderValue.value} nights`;
    } else if (minDays.value === maxDays.value) {
      return minDays.value === 1 ? `${maxDays.value} night` : `${maxDays.value} nights`;
    } else {
      return `${minDays.value} - ${maxDays.value} nights`;
    }
  }
  return '';
});

// Quick-pick night presets, clamped to the available window between dates
const nightPresets = computed(() => {
  const cap = maxSliderValue.value;
  const raw = [
    { label: 'Weekend', min: 2, max: 3 },
    { label: 'Short break', min: 3, max: 5 },
    { label: 'One week', min: 6, max: 8 },
    { label: 'Two weeks', min: 12, max: 15 },
  ];
  return raw
    .filter((p) => p.min <= cap)
    .map((p) => ({ label: p.label, min: Math.min(p.min, cap), max: Math.min(p.max, cap) }));
});

const applyPreset = (preset) => {
  minDays.value = Math.max(0, Math.min(preset.min, maxSliderValue.value));
  maxDays.value = Math.min(preset.max, maxSliderValue.value);
  nightsManuallyEdited.value = true;
};

const isPresetActive = (preset) =>
  minDays.value === preset.min && maxDays.value === preset.max;

const clampMinDays = () => {
  if (isNaN(minDays.value) || minDays.value < 0) minDays.value = 0;
  if (minDays.value > maxSliderValue.value) minDays.value = maxSliderValue.value;
  if (minDays.value > maxDays.value) minDays.value = maxDays.value;
  nightsManuallyEdited.value = true;
};

const clampMaxDays = () => {
  if (isNaN(maxDays.value) || maxDays.value < 0) maxDays.value = 0;
  if (maxDays.value > maxSliderValue.value) maxDays.value = maxSliderValue.value;
  if (maxDays.value < minDays.value) maxDays.value = minDays.value;
  nightsManuallyEdited.value = true;
};

const updateMinDays = (value) => {
  minDays.value = Math.max(0, Math.min(value, maxDays.value));
  nightsManuallyEdited.value = true;
};

const updateMaxDays = (value) => {
  maxDays.value = Math.min(maxSliderValue.value, Math.max(value, minDays.value));
  nightsManuallyEdited.value = true;
};

const clearStartDate = () => {
  selectedDates.value.start = null;
  selectedDates.value.end = null;
  emit('update-selected-dates', selectedDates.value);
};

const clearEndDate = () => {
  selectedDates.value.end = null;
  emit('update-selected-dates', selectedDates.value);
};

// Watch for changes in props.selectedDates
watch(
  () => props.selectedDates,
  (newDates) => {
    selectedDates.value = newDates;
    console.log('Selected dates vertical', selectedDates.value);
  },
  { immediate: true, deep: true },
);

// Watch for changes in props.exactDays
watch(
  () => props.exactDays,
  (newValue) => {
    exactDays.value = newValue;
    dateMode.value = newValue ? 'exact' : 'range';
  },
  { immediate: true },
);
</script>

<style scoped>
/* Ensure focus outlines are visible */
div[role="button"]:focus,
div[role="slider"]:focus,
input:focus,
button:focus,
select:focus {
  outline: 2px solid #3b82f6; /* Tailwind's blue-500 */
  outline-offset: 2px;
}

/* Optional: Ensure the popup appears above other elements */
.fixed {
  z-index: 50;
}

/* Highlight error fields */
.border-red-500 {
  border-color: #f87171; /* Tailwind's red-500 */
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.8s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.hint-enter-active, .hint-leave-active {
  transition: opacity 0.18s ease;
}
.hint-enter-from, .hint-leave-to {
  opacity: 0;
}

</style>
